/**
 * DS-2 §3.1 — the states table, defined ONCE for every control.
 *
 * This exists so that "what a selected thing looks like" is a single fact in
 * the codebase rather than a convention repeated in six renderers. Every
 * control in the app consumes these; none of them describe a state themselves.
 *
 * | state         | rule                                                        |
 * | default       | --surface-raised, 1px --control-border, --ink-900 label      |
 * | pressed       | --surface-sunken, no size change, no transition              |
 * | selected      | 2px --accent, --accent-tint background, filled mark          |
 * | focus-visible | 2px --accent outline, 2px offset, on top of any other state  |
 * | disabled      | --ink-500 label, --surface-sunken, --line border             |
 * | failed        | inline --critical message below the item, answer retained    |
 *
 * NO HOVER STATE. The target is a phone. Any hover styling would have to live
 * inside @media (hover: hover) and could never be the only signal.
 *
 * THE NEUTRALITY RULE, and it is why these are constants rather than functions
 * of the option: within one item, every option resolves to the same tokens in
 * the same state. Selected option 1 and selected option 5 are pixel-identical
 * apart from position and label. Nothing here may ever be keyed on option
 * index, stored value, or reverse-scored flag.
 */

/**
 * The mark: a 22px circle, --control-border 1px unselected, --accent 2px with a
 * 10px filled centre selected. Drawn rather than a native control, because a
 * native radio cannot be sized reliably across platforms — but the real
 * <input> is always present and is what carries state and semantics.
 */
export function markClasses({ selected, disabled }) {
  const ring = disabled
    ? 'border-[var(--line)]'
    : selected
      ? 'border-2 border-[var(--accent)]'
      : 'border border-[var(--control-border)]'
  // pointer-events-none: the mark is decorative and aria-hidden, so it must
  // never be the thing a tap lands on. The <label> is the target.
  /*
   * THE MARK SCALES WITH THE TEXT. 1.375rem, not 22px.
   *
   * appdesign corrected its own §1 here and it is the load-bearing one: the
   * mark is the ONLY selection channel that survives forced-colors — the thing
   * §7.4 proved. Pinned at 22px, a 200% system font doubles the label to 32px
   * while the mark stays put, so the single channel the accessibility floor
   * depends on becomes proportionally half-size, on the screen she uses thirty
   * times.
   *
   * THE RULE, applied throughout: anything sitting beside text scales with it.
   * Radii, hairlines and the 3px leading bar stay px — they are edges, not
   * content, and an edge that grew with the font would just get heavier.
   *
   * The border widths above are deliberately NOT converted: 1px vs 2px is the
   * geometric difference forced-colors preserves, and a fractional rem border
   * rounds unpredictably at some zoom levels.
   */
  return `pointer-events-none inline-flex h-[1.375rem] w-[1.375rem] shrink-0 items-center justify-center rounded-[var(--r-pill)] ${ring}`
}

export function markDotClasses({ selected, disabled }) {
  if (!selected) return 'hidden'
  // The filled centre scales with its ring, or the dot shrinks inside a growing
  // circle and the selected state gets quieter as her text gets larger.
  return `h-[0.625rem] w-[0.625rem] rounded-[var(--r-pill)] ${disabled ? 'bg-[var(--ink-500)]' : 'bg-[var(--accent)]'}`
}

/**
 * A text field. 48px minimum, and never full width where the value is short —
 * a 3-digit field spanning the screen invites a sentence.
 */
export function fieldClasses({ invalid, disabled }) {
  const border = invalid
    ? 'border border-[var(--critical)]'
    : disabled
      ? 'border border-[var(--line)]'
      : 'border border-[var(--control-border)]'
  /*
   * F9 — h-12, NOT min-h-12, so every control is exactly 48px.
   *
   * Demographics measured 50 / 50 / 49 / 48 for input / time / select / button:
   * four heights on one screen, none of them agreeing. min-h-12 sets a FLOOR of
   * 48 and box-sizing then adds the 1px border on each side to the 24px line
   * box plus 24px of padding, so a bordered control lands at 50 while the
   * borderless button lands at 48. The floor was doing nothing except letting
   * each control settle wherever its own box maths put it.
   *
   * These are all single-line controls, so a fixed height is safe and is the
   * only thing that makes them agree. Buttons keep min-h-12 because their label
   * may wrap.
   */
  return [
    'h-12 rounded-[var(--r-control)] px-4 py-3',
    disabled ? 'bg-[var(--surface-sunken)] text-[var(--ink-500)]' : 'bg-[var(--surface-raised)]',
    border,
  ].join(' ')
}

/**
 * Buttons — AD-3 §4.3. THREE TYPES AND THERE IS NO FOURTH.
 *
 *   primary    the ONE forward action of a screen. --accent fill, no border.
 *   secondary  an action of standing that is NOT the way forward.
 *   quiet      navigation and the two unconditional affordances.
 *
 * There is deliberately no destructive type. F11 wants "Leave the study" told
 * apart from "Sign out", and colour is the wrong instrument: the palette has no
 * red for it, and inventing one would make leaving look punished — the opposite
 * of what consent promised. It is ranked by architecture instead.
 *
 * SIZING IS DERIVED FROM THE TYPE RAMP, not chosen beside it. The height is
 * 1.4em + 2x --s-3, FLOORED at 48px — 48 at the default root, 52 at 125%, 68.8
 * at 200%. So the button grows with her text instead of clipping its own label,
 * which is F8's failure class wearing a different hat. `min-h`, never `h`: any
 * rule that sets a fixed height reintroduces the bug.
 *
 * PRESSED IS INSTANT — no transition, no transform, no shadow, no ripple. On
 * the primary it is an OKLCH lightness drop of 0.456 to 0.386, unmistakable in
 * daylight and costing nothing in the answering path, where DS-2 §1.6 forbids
 * anything between a tap and a recorded answer.
 *
 * FOCUS WORKS ON THE PRIMARY BECAUSE OF THE OFFSET: the ring is --accent on a
 * button that is already --accent, but the 2px gap shows --surface between
 * them, and accent/surface is 6.29:1.
 */
export function buttonClasses(kind = 'primary', { incomplete = false } = {}) {
  const base =
    'rounded-[var(--r-control)] text-base leading-[1.4] ' +
    'disabled:cursor-default'
  const full = 'w-full min-h-[max(48px,3rem)] px-[var(--s-5)] py-[var(--s-3)] font-semibold'

  if (kind === 'quiet') {
    /*
     * Quiet is permitted in exactly three places — the NavBar left slot, the
     * ActionBar footnote row, and "Take me to it" inside the outstanding note.
     * NEVER inline in the body. That one restriction is most of what stops
     * buttons appearing in random places.
     */
    return (
      /*
       * 44px IN PX, NOT REM, AND THAT IS NOT AN INCONSISTENCY.
       *
       * The mark is rem because it sits beside text and is read WITH it. A
       * touch target is a different kind of number: it is the size of a thumb,
       * and a thumb does not get bigger when she raises her font size. As rem
       * this became 88px at 200%, so two stacked quiet links pushed the
       * ActionBar to 275px — 33% of the viewport, worse than the 241px it was
       * meant to fix.
       *
       * AD-3 §2.3 states targets in px for exactly this reason. Content scales;
       * ergonomics do not.
       */
      `${base} inline-flex min-h-[max(44px,2.75rem)] w-auto items-center justify-center p-[var(--s-2)] ` +
      'font-normal text-[var(--accent)] active:bg-[var(--surface-sunken)] ' +
      'disabled:text-[var(--ink-500)]'
    )
  }

  if (kind === 'primary') {
    /*
     * THE PRIMARY IS NEVER DISABLED, and that is the change.
     *
     * `Continue` on day 0 was disabled:false wearing the disabled palette —
     * grey but tappable, because tapping it is how the outstanding note
     * appears. It read as broken. Consent's was genuinely disabled: a grey slab
     * at the bottom of the most important screen in the study.
     *
     * A primary is always live and always explains. Pressing it on an
     * incomplete screen does not submit; it reveals the note and moves focus to
     * the first item that needs an answer. That is DS-2 §3.8's own rule — the
     * incomplete state is not entered until she asks to leave — finally applied
     * to the CONTROL as well as to the note.
     *
     * The no-skip rule is untouched: nothing is submitted, the client never
     * recomputes required_outstanding, and the server stays the only authority
     * on completeness. `incomplete` therefore changes no paint at all now; it
     * survives only to carry aria-disabled for assistive tech.
     */
    /*
     * AD-5 defect 2 — DISABLED MUST NOT PAINT AS LIVE.
     *
     * `buttonClasses('primary')` had no disabled: variants at all, so consent's
     * genuinely-disabled primary rendered full --accent at opacity 1: pressing
     * changed nothing, explained nothing, and it was indistinguishable from a
     * live button. Worse than the grey slab it replaced, which at least read as
     * not-ready — on the consent screen.
     *
     * A primary should almost never be disabled (see below), but where it is —
     * a submit in flight — it must say so. --ink-500 on --surface-sunken is
     * 5.18:1, so it stays legible rather than becoming a ghost.
     */
    return (
      `${base} ${full} bg-[var(--accent)] text-[var(--on-accent)] ` +
      'active:bg-[var(--accent-pressed)] ' +
      'disabled:bg-[var(--surface-sunken)] disabled:text-[var(--ink-500)] ' +
      'disabled:border disabled:border-[var(--line)]'
    )
  }

  /*
   * Secondary: zero or more per screen, but only as siblings of equal standing
   * — the resource buttons on a safety surface are the case this exists for. A
   * SECONDARY IS NEVER THE SCREEN'S WAY FORWARD, which is what F4 was pointing
   * at: /support and /paused rendered their sole forward action as an outline.
   *
   * Disabled survives here and on quiet, for controls that are genuinely
   * inapplicable, and it is specified so that when it appears it is legible:
   * --ink-500 on --surface-sunken is 5.18:1, not a ghost.
   */
  return (
    `${base} ${full} bg-[var(--surface-raised)] text-[var(--ink-900)] ` +
    'border border-[var(--control-border)] active:bg-[var(--surface-sunken)] ' +
    'disabled:bg-[var(--surface-sunken)] disabled:text-[var(--ink-500)] ' +
    'disabled:border-[var(--line)]'
  )
}

/**
 * FLOATING LABEL FIELD — the label lives INSIDE the control and rises out of
 * the way when the field is focused or filled.
 *
 * WHY: a static label above every input costs ~24px of vertical space per
 * field. On a phone with the keyboard up that is the difference between the
 * primary action being reachable and being under the keyboard. The label
 * becomes a micro-header instead of vanishing like a placeholder, so she never
 * loses track of what she is typing.
 *
 * EVERY MEASUREMENT HERE IS RELATIVE, AND THAT IS THE WHOLE DESIGN.
 *
 * The reference pattern is specified in px: a 56px box with a 12px label. This
 * app has to survive a 200% system font, and that is not theoretical - it is
 * why `markClasses` is 1.375rem rather than 22px, and why `gridCapacity.js`
 * exists at all. At 200% the body text is ~32px, and a rigid 56px box cannot
 * hold 32px of input text with a shrunken label above it. The box would have
 * to grow, and the vertical saving would evaporate exactly when space is
 * tightest.
 *
 * So the box is `max(56px, 3.5rem)` - the same shape as buttonClasses, a px
 * FLOOR for thumbs and rem GROWTH for text. The label size, the distance it
 * travels and the padding that receives it are all `em`, relative to the
 * field's own font size, so the assembly scales as one object.
 *
 * FONT-SIZE, NOT transform: scale(). A scaled label resamples the glyphs and
 * goes soft, which on a 0.75em label is the difference between readable and
 * not. Transitioning font-size and translate costs a little more than a pure
 * compositor transform, and this is a form, not the answering path - DS-2
 * §1.6 forbids animation between a tap and a recorded answer, and this is
 * neither.
 *
 * THE RESTING LABEL IS NOT A PLACEHOLDER. The documented failure of this
 * pattern is a low-contrast resting label that doubles as a hint and disappears
 * under the value. --ink-700 on --surface-raised is a real text colour, not a
 * ghost, and this is a clinical instrument read by someone who has not slept.
 */
export function floatFieldClasses({ invalid, disabled }) {
  const border = invalid
    ? 'border border-[var(--critical)]'
    : disabled
      ? 'border border-[var(--line)]'
      : 'border border-[var(--control-border)]'
  return [
    'relative w-full min-h-[max(56px,3.5rem)] rounded-[var(--r-control)]',
    disabled ? 'bg-[var(--surface-sunken)]' : 'bg-[var(--surface-raised)]',
    border,
  ].join(' ')
}

/** The label, at rest (centred, body size) or floated (top, smaller). */
export function floatLabelClasses({ invalid, disabled }) {
  const colour = invalid
    ? 'text-[var(--critical)]'
    : disabled
      ? 'text-[var(--ink-500)]'
      : 'text-[var(--ink-700)]'
  /*
   * DRIVEN BY CSS, NOT BY REACT STATE, AND THAT IS A BUG FIX.
   *
   * The first version floated on `focused || value !== ''`, both read from
   * React. BROWSER AUTOFILL DOES NOT FIRE onChange, so a password manager
   * filling the form left `value` empty, the label stayed at rest, and it sat
   * directly on top of the filled-in text. That is the screenshot: two strings
   * in the same place on the sign-in screen.
   *
   * The peer selectors ask the DOM instead, so every way a field can acquire a
   * value is covered - typing, autofill, paste, a restored session:
   *   peer-focus                       she is in it
   *   peer-[:not(:placeholder-shown)]  it has content (needs placeholder=" ")
   *   peer-autofill                    the browser filled it
   *
   * No state, no effect, nothing to keep in sync. The label element must come
   * AFTER the input in the DOM for `peer-` to reach it; it is positioned
   * absolutely, so source order is not visual order.
   */
  return [
    'pointer-events-none absolute left-4 origin-left',
    'transition-[top,font-size,color] duration-200 ease-in-out',
    'motion-reduce:transition-none',
    // Resting: vertically centred, body size.
    'top-1/2 -translate-y-1/2 text-base leading-none',
    // Floated: any of the three conditions.
    'peer-focus:top-[0.5em] peer-focus:translate-y-0 peer-focus:text-[0.75em]',
    'peer-[:not(:placeholder-shown)]:top-[0.5em] peer-[:not(:placeholder-shown)]:translate-y-0 peer-[:not(:placeholder-shown)]:text-[0.75em]',
    'peer-autofill:top-[0.5em] peer-autofill:translate-y-0 peer-autofill:text-[0.75em]',
    colour,
  ].join(' ')
}

/** The input itself: transparent, borderless, padded to clear the label. */
export function floatInputClasses({ disabled }) {
  return [
    'peer w-full bg-transparent text-base outline-none',
    'px-4 pt-[1.6em] pb-[0.4em] min-h-[max(56px,3.5rem)]',
    disabled ? 'text-[var(--ink-500)]' : 'text-[var(--ink-900)]',
  ].join(' ')
}
